import {
  type AddItemSetQuestionQConfigStructureLocationType,
  GetQConfigByIdDocument,
  useAddItemSetQuestionQConfigStructureMutation,
  useGetItemSetQuestionPropertiesCountByLocationLazyQuery,
} from '@exam-management/graphql';
import { APP_PERMISSIONS } from '@exam-management/permissions';
import { useParams } from '@exam-management/router';
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Flex,
  Form,
  Text,
} from '@shared/components';
import { useHasPermission } from '@shared/permissions';
import { useSnackbar } from '@shared/snackbar';
import { useEffect, useState } from 'react';
import { type Path } from 'react-hook-form';

import {
  ChapterInfoSection,
  ItemSetApplyConditionErrorAlert,
  ItemSetQuestionConditionsTable,
  QConfigItemSetQuestionConditionsFormFields,
  QuestionSummarySection,
  type TableData,
} from '../../components';
import { useFetchLos, useGetSelectedLOSForItemSet } from '../../hooks';
import {
  type AddItemSetQuestionConditionsFormType,
  useAddItemSetQuestionConditions,
  useAddItemSetQuestionConditionsFormContext,
} from '../../providers';
import { type ModalContentMapType } from '../../types';
import {
  calculateSummaryData,
  formatConditions,
  formatTableData,
  formatTopicsOrSubtopics,
  getLocationTypeByContentType,
  setErrorsByInvalidConditions,
  validateItemSetConditions,
  validateLOSSelectionAndSetError,
} from '../../utils';
import { QConfigItemSetQuestionPropertiesFormFields } from '../item-set-question-properties-form-fields';

interface AddItemSetQuestionContentProps {
  onClose: () => void;
  onSubmit?: () => void;
  setModalContent: (content: keyof ModalContentMapType) => void;
}

export default function AddItemSetQuestionContent(props: Readonly<AddItemSetQuestionContentProps>) {
  const { onClose, setModalContent } = props;
  const {
    chapterData,
    levelItemsWithLevel,
    structures: qConfigStructures,
  } = useAddItemSetQuestionConditions();
  const formContext = useAddItemSetQuestionConditionsFormContext();
  const { watch, handleSubmit, trigger, getValues, setError, clearErrors } = formContext;
  const goToSelectTopicContent = () => {
    setModalContent('SELECT_TOPIC_CONTENT');
  };
  const { qconfigId: qConfigId } = useParams('/exam-courses/:courseId/qconfigs/:qconfigId/details');

  const { contentType, location, itemSetType, los } = watch();
  const [appliedConditions, setAppliedConditions] = useState<boolean>(false);
  const [showAppliedConditionsError, setShowAppliedConditionsError] = useState<boolean>(false);
  const [summaryData, setSummaryData] = useState<{
    totalItemSetQuestions: string;
    totalScore: number;
    selectedQuestions: number;
  } | null>(null);

  const [questionConditionsData, setQuestionConditionsData] = useState<
    { data: TableData; total?: number }[]
  >([
    {
      data: { calculated: {}, notCalculated: {} },
    },
  ]);
  const existingLOS = useGetSelectedLOSForItemSet({ chapterId: chapterData.id, qConfigStructures });

  const hasViewPermission = useHasPermission(APP_PERMISSIONS.QBANK.LOS.VIEW);
  const { showSnackbar } = useSnackbar();
  const [
    getItemSetQuestionPropertiesCountByLocation,
    {
      data: getItemSetQuestionPropertiesCountResult,
      loading: isGetItemSetQuestionPropertiesCountLoading,
    },
  ] = useGetItemSetQuestionPropertiesCountByLocationLazyQuery({
    fetchPolicy: 'network-only',
  });

  const [
    addItemSetQuestionQConfigStructure,
    { loading: isAddItemSetQuestionQConfigStructureLoading },
  ] = useAddItemSetQuestionQConfigStructureMutation({
    onCompleted: () => {
      showSnackbar({
        message: 'Add Item Set Question Successfully',
        variant: 'success',
      });
    },
    onError: () => {
      showSnackbar({
        message: 'Unable to Add Item Set Question',
        variant: 'error',
      });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: GetQConfigByIdDocument,
        variables: {
          getQConfigByIdInput: {
            qConfigId,
          },
        },
      },
    ],
  });
  const subtopics = location.subtopics;

  const selectedSubtopics = Object.entries(subtopics ?? {})
    .filter(([, value]) => value)
    .map(([key]) => key);

  const selectedLOS = los?.map((los) => los.value) ?? [];

  const isLocationSelected = selectedSubtopics.length > 0 || selectedLOS.length > 0;

  const { options, isSearchLOSLoading } = useFetchLos({
    chapterLevelItemId: chapterData.refId,
    contentType,
  });

  const noOptionsText = !hasViewPermission
    ? 'ไม่มีสิทธิ์การเข้าถึง กรุณาติดต่อผู้ดูแลระบบ'
    : 'ไม่พบข้อมูล';

  const handleSave = async (data: AddItemSetQuestionConditionsFormType) => {
    if (!appliedConditions) {
      setShowAppliedConditionsError(true);
      throw new Error('Validate failed: Please apply conditions before saving.');
    }

    const itemSetConditionsValidation = validateItemSetConditions({
      conditions: data.conditions,
      queryResult:
        getItemSetQuestionPropertiesCountResult?.qctGetItemSetQuestionPropertiesCountByLocation,
    });

    if (!itemSetConditionsValidation.isValid) {
      setErrorsByInvalidConditions(itemSetConditionsValidation.results.invalidConditions, setError);
      throw new Error('Validate failed: Conditions are not valid.');
    }

    if (!validateLOSSelectionAndSetError(data.los ?? [], existingLOS, setError)) {
      throw new Error('Validate failed: LOS selection is not valid.');
    }

    const conditions = formatConditions(data.conditions);

    const formattedLocation =
      contentType === 'TOPIC'
        ? formatTopicsOrSubtopics(data.location.subtopics)
        : (data.los?.map((item) => item.value) ?? []);

    await addItemSetQuestionQConfigStructure({
      variables: {
        addItemSetQuestionQConfigStructureInput: {
          qConfigId,
          chapterLevelItemId: chapterData.refId ?? '',
          conditions,
          scorePerQuestion: data.scorePerQuestion,
          size: parseInt(data.itemSetType as unknown as string),
          location: {
            locationType: getLocationTypeByContentType(
              data.contentType,
            ) as unknown as AddItemSetQuestionQConfigStructureLocationType,
            losIds: contentType === 'LOS' ? formattedLocation : undefined,
            qBankStructureLevelItemIds: contentType === 'TOPIC' ? formattedLocation : undefined,
          },
        },
      },
    });
    onClose();
  };

  const handleApplyConditions = async (data: AddItemSetQuestionConditionsFormType) => {
    setShowAppliedConditionsError(false);
    const isValid = await trigger('conditions');
    if (!isValid) return;
    const itemSetSize = parseInt(data.itemSetType as unknown as string);

    const formattedLocation =
      contentType === 'TOPIC'
        ? formatTopicsOrSubtopics(data.location.subtopics)
        : (data.los?.map((item) => item.value) ?? []);

    const { data: queryResult } = await getItemSetQuestionPropertiesCountByLocation({
      variables: {
        input: {
          type: getLocationTypeByContentType(contentType),
          size: itemSetSize,
          losIds: contentType === 'LOS' ? formattedLocation : undefined,
          qBankStructureLevelItemIds: contentType === 'TOPIC' ? formattedLocation : undefined,
        },
      },
    });
    setAppliedConditions(true);

    const itemSetConditionsValidation = validateItemSetConditions({
      conditions: formContext.getValues('conditions'),
      queryResult: queryResult?.qctGetItemSetQuestionPropertiesCountByLocation,
    });

    if (!itemSetConditionsValidation.isValid) {
      setErrorsByInvalidConditions(itemSetConditionsValidation.results.invalidConditions, setError);
      setQuestionConditionsData(
        formatTableData(itemSetConditionsValidation.results.validConditions),
      );
    } else {
      const scorePerQuestion = data.scorePerQuestion ?? 0;
      setQuestionConditionsData(
        formatTableData(itemSetConditionsValidation.results.validConditions),
      );
      setSummaryData(
        calculateSummaryData({
          itemSetType: itemSetSize,
          allQueriedQuestions: itemSetConditionsValidation.results.allQueriedQuestions,
          totalValidQuestions: itemSetConditionsValidation.results.totalValidQuestions,
          scorePerQuestion,
        }),
      );
    }
  };

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name?.includes('conditions') || name === 'los' || name?.includes('location')) {
        setAppliedConditions(false);
        setShowAppliedConditionsError(false);

        const splittedConditionName = name.split('.');
        if (splittedConditionName.length > 1) {
          const targetPath = splittedConditionName.slice(0, 2).join('.');
          clearErrors(targetPath as Path<AddItemSetQuestionConditionsFormType>);
        }
      }

      if (name === 'scorePerQuestion') {
        if (value.itemSetType && value.scorePerQuestion) {
          const itemSetConditionsValidation = validateItemSetConditions({
            conditions: formContext.getValues('conditions'),
            queryResult:
              getItemSetQuestionPropertiesCountResult?.qctGetItemSetQuestionPropertiesCountByLocation,
          });

          if (itemSetConditionsValidation.isValid) {
            setSummaryData(
              calculateSummaryData({
                itemSetType: parseInt(value.itemSetType as unknown as string),
                allQueriedQuestions: itemSetConditionsValidation.results.allQueriedQuestions,
                totalValidQuestions: itemSetConditionsValidation.results.totalValidQuestions,
                scorePerQuestion: value.scorePerQuestion ?? 0,
              }),
            );
          }
        }
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, getItemSetQuestionPropertiesCountResult, setSummaryData]);

  const isSubmitDisabled = !isLocationSelected || !itemSetType;

  return (
    <Form>
      <DialogTitle onClose={onClose}>Add Item Set Question</DialogTitle>
      <Divider />
      <DialogContent sx={{ height: '600px' }}>
        <Flex sx={{ flexDirection: 'column', gap: (theme) => theme.spacing(4) }}>
          <ChapterInfoSection
            title={chapterData?.name ?? 'บท: ไม่ระบุ'}
            qSource={chapterData?.refCode ?? '-'}
            questionType='item-set'
          />
          <QConfigItemSetQuestionConditionsFormFields
            hideContentSelector={!itemSetType || !contentType}
            slotProps={{
              contentSelectorByContentType: {
                onEdit: goToSelectTopicContent,
                location,
                contentType,
                levelItemsWithLevel,
                group: {
                  options,
                  loading: isSearchLOSLoading,
                  noOptionsText,
                  hasViewPermission,
                  existingLOS,
                },
                questionSize: itemSetType,
              },
            }}
          />
          {isLocationSelected ? (
            <>
              <QConfigItemSetQuestionPropertiesFormFields
                itemSetType={itemSetType}
                onApplyConditions={() => handleApplyConditions(getValues())}
                isLoading={isGetItemSetQuestionPropertiesCountLoading}
                formContext={formContext}
              />
              <Text variant='body1Semibold'>จำนวนข้อสอบที่มีในคลัง (ตามระดับความยาก)</Text>
              <QuestionSummarySection
                totalItemSetQuestions={summaryData?.totalItemSetQuestions}
                totalScore={summaryData?.totalScore}
                selectedQuestions={summaryData?.selectedQuestions}
              />
              {showAppliedConditionsError ? <ItemSetApplyConditionErrorAlert /> : null}
              <ItemSetQuestionConditionsTable
                questionConditionsData={questionConditionsData}
                questionSize={itemSetType}
              />
            </>
          ) : null}
        </Flex>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Button onClick={onClose} size='large' type='button' color='secondary' variant='outlined'>
          Cancel
        </Button>
        <Button
          size='large'
          type='button'
          onClick={handleSubmit(handleSave)}
          loading={isAddItemSetQuestionQConfigStructureLoading}
          disabled={isSubmitDisabled}
        >
          Save
        </Button>
      </DialogActions>
    </Form>
  );
}
